import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { components } from '@trackercop/contracts';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { useCurrentIdentity } from '@/lib/identity-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';

type AlertSubscription = components['schemas']['AlertSubscription'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const siteAlertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId/alerts',
  component: AlertSubscriptionsPage,
});

function alertsQueryKey(siteId: string) {
  return ['sites', siteId, 'alert-subscriptions'] as const;
}

function AlertSubscriptionsPage() {
  const { siteId } = siteAlertsRoute.useParams();
  const { hasPermission } = useCurrentIdentity();
  const canWrite = hasPermission('alerts:manage');
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: alertsQueryKey(siteId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/alert-subscriptions', {
        params: { path: { siteId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { email: string }) => {
      const { data, error } = await apiClient.POST('/sites/{siteId}/alert-subscriptions', {
        params: { path: { siteId } },
        body: {
          subscriberEmail: payload.email,
          triggerNewTracker: true,
          triggerNewPayloadCategory: true,
          triggerDrift: true,
          enabled: true,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsQueryKey(siteId) });
    },
  });

  const items = listQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to="/sites/$siteId"
          params={{ siteId }}
          className="text-xs text-muted-foreground hover:underline"
          data-testid="site-alerts-back-link"
        >
          ← Back to site
        </Link>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Alert subscribers</CardTitle>
          <p className="text-xs text-muted-foreground">
            Subscribers receive email alerts for enabled triggers. Triggers update inline; deletions are
            immediate.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {canWrite && (
            <AddSubscriberForm
              onAdd={(email) => createMutation.mutate({ email })}
              pending={createMutation.isPending}
              error={createMutation.isError ? 'Could not add subscriber.' : null}
            />
          )}
          {listQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading subscribers…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No subscribers configured.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Email</TH>
                  <TH>New tracker</TH>
                  <TH>New category</TH>
                  <TH>Drift</TH>
                  <TH>Enabled</TH>
                  <TH className="w-8"></TH>
                </TR>
              </THead>
              <TBody>
                {items.map((sub) => (
                  <SubscriberRow key={sub.id} siteId={siteId} subscription={sub} canWrite={canWrite} />
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AddSubscriberForm({
  onAdd,
  pending,
  error,
}: {
  onAdd: (email: string) => void;
  pending: boolean;
  error: string | null;
}) {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const valid = EMAIL_REGEX.test(email);
  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (!valid) return;
        onAdd(email);
        setEmail('');
        setTouched(false);
      }}
    >
      <label className="flex flex-1 flex-col gap-1 text-xs font-medium">
        New subscriber email
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="name@company.com"
          maxLength={254}
          data-testid="alert-new-email"
        />
        {touched && !valid && (
          <span className="text-[10px] text-destructive" data-testid="alert-email-error">
            Enter a valid email address.
          </span>
        )}
        {error && <span className="text-[10px] text-destructive">{error}</span>}
      </label>
      <Button type="submit" disabled={!valid || pending} data-testid="alert-add-submit">
        {pending ? 'Adding…' : 'Add'}
      </Button>
    </form>
  );
}

function SubscriberRow({
  siteId,
  subscription,
  canWrite,
}: {
  siteId: string;
  subscription: AlertSubscription;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: async (patch: Partial<AlertSubscription>) => {
      const { data, error } = await apiClient.PUT('/alert-subscriptions/{subscriptionId}', {
        params: { path: { subscriptionId: subscription.id } },
        body: patch,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsQueryKey(siteId) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE('/alert-subscriptions/{subscriptionId}', {
        params: { path: { subscriptionId: subscription.id } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertsQueryKey(siteId) });
    },
  });

  return (
    <TR data-testid={`alert-row-${subscription.id}`}>
      <TD className="font-mono text-xs">{subscription.subscriberEmail}</TD>
      <TD>
        <input
          type="checkbox"
          checked={subscription.triggerNewTracker}
          disabled={!canWrite || updateMutation.isPending}
          onChange={(e) => updateMutation.mutate({ triggerNewTracker: e.target.checked })}
          data-testid={`alert-trigger-new-tracker-${subscription.id}`}
        />
      </TD>
      <TD>
        <input
          type="checkbox"
          checked={subscription.triggerNewPayloadCategory}
          disabled={!canWrite || updateMutation.isPending}
          onChange={(e) => updateMutation.mutate({ triggerNewPayloadCategory: e.target.checked })}
          data-testid={`alert-trigger-new-category-${subscription.id}`}
        />
      </TD>
      <TD>
        <input
          type="checkbox"
          checked={subscription.triggerDrift}
          disabled={!canWrite || updateMutation.isPending}
          onChange={(e) => updateMutation.mutate({ triggerDrift: e.target.checked })}
          data-testid={`alert-trigger-drift-${subscription.id}`}
        />
      </TD>
      <TD>
        <input
          type="checkbox"
          checked={subscription.enabled}
          disabled={!canWrite || updateMutation.isPending}
          onChange={(e) => updateMutation.mutate({ enabled: e.target.checked })}
          data-testid={`alert-enabled-${subscription.id}`}
        />
      </TD>
      <TD>
        {canWrite && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            data-testid={`alert-delete-${subscription.id}`}
          >
            Remove
          </Button>
        )}
      </TD>
    </TR>
  );
}
